import React, { createContext, useContext, useMemo, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { createThemeWithMode } from "../theme/theme";
import { enUS, deDE, frFR, hiIN } from "@mui/material/locale";
import { useTranslation } from "react-i18next";
import RTLWrapper from "../components/RTLWrapper";

// Define default values outside the ColorModeProvider
const DEFAULT_MODE = "light";
const DEFAULT_DIRECTION = "ltr";
const DEFAULT_FONT_FAMILY = "Kanit";
const DEFAULT_PRIMARY_COLOR = "#1976d2";
const DEFAULT_BORDER_RADIUS = 14;

const ColorModeContext = createContext<any>({
  toggleColorMode: () => {},
  changeLanguage: (lang: string) => {},
  toggleDirection: () => {},
  handleResetSettings: () => {},
});

export const useColorMode = () => useContext(ColorModeContext);

export const ColorModeProvider = ({ children }: any) => {
  const { i18n } = useTranslation<any>();
  const [muiLocale, setMuiLocale] = useState(enUS);

  // Add new state hooks for fontFamily, primaryColor, and borderRadius
  const [mode, setMode] = useState<"light" | "dark">(DEFAULT_MODE);
  const [direction, setDirection] = useState<"ltr" | "rtl">(DEFAULT_DIRECTION);
  const [fontFamily, setFontFamily] = useState<string>(DEFAULT_FONT_FAMILY);
  const [primaryColor, setPrimaryColor] = useState<string>(
    DEFAULT_PRIMARY_COLOR
  );
  const [borderRadius, setBorderRadius] = useState<number>(
    DEFAULT_BORDER_RADIUS
  );

  const toggleDirection = () => {
    setDirection((prevDirection) => (prevDirection === "ltr" ? "rtl" : "ltr"));
  };

  // Reset function
  const handleResetSettings = () => {
    setMode(DEFAULT_MODE);
    setDirection(DEFAULT_DIRECTION);
    setPrimaryColor(DEFAULT_PRIMARY_COLOR);
    setBorderRadius(DEFAULT_BORDER_RADIUS);
    setFontFamily(DEFAULT_FONT_FAMILY);
  };

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
      },
      changeLanguage: (lang: string) => {
        i18n.changeLanguage(lang);
        switch (lang) {
          case "de":
            setMuiLocale(deDE);
            break;
          case "fr":
            setMuiLocale(frFR);
            break;
          case "hi":
            setMuiLocale(hiIN);
            break;
          default:
            setMuiLocale(enUS);
            break;
        }
      },
      toggleDirection,
      setFontFamily: (font: string) => setFontFamily(font),
      setPrimaryColor: (color: string) => setPrimaryColor(color),
      setBorderRadius: (radius: number) => setBorderRadius(radius),
      handleResetSettings,
    }),
    [i18n]
  );

  const theme = useMemo(
    () =>
      createThemeWithMode(
        mode,
        muiLocale,
        direction,
        fontFamily,
        primaryColor,
        borderRadius
      ),
    [mode, muiLocale, direction, fontFamily, primaryColor, borderRadius]
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <RTLWrapper direction={theme.direction}>
        <ThemeProvider theme={theme}>{children}</ThemeProvider>
      </RTLWrapper>
    </ColorModeContext.Provider>
  );
};
